/**
 * Cost Reports Component
 *
 * Provides comprehensive cost tracking and reporting for AI service usage.
 * Features:
 * - Date range selection for cost reports
 * - Cost breakdown by usage type (LLM, TTS, STT, Image, etc.)
 * - Cost breakdown by bot
 * - Cost breakdown by model
 * - Daily cost trend chart
 * - Projected monthly costs
 * - Export functionality
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

/**
 * Usage type enum matching backend
 */
type UsageType = 'LLM_CHAT' | 'TRANSCRIPTION' | 'TTS' | 'IMAGE' | 'EMBEDDING' | 'SEARCH';

/**
 * Cost breakdown by usage type
 */
interface CostBreakdown {
  type: UsageType;
  count: number;
  totalTokens: number;
  totalDuration: number;
  calculatedCost: number;
  recordedCost: number;
}

/**
 * Cost breakdown by bot
 */
interface BotCostBreakdown {
  botId: string;
  botName: string;
  cost: number;
  tokenCount: number;
  requestCount: number;
}

/**
 * Cost breakdown by model
 */
interface ModelCostBreakdown {
  model: string;
  cost: number;
  count: number;
}

/**
 * Daily cost data
 */
interface DailyCost {
  date: string;
  cost: number;
  count: number;
}

/**
 * Cost report data from API
 */
interface CostReport {
  periodStart: string;
  periodEnd: string;
  totalCost: number;
  totalRecordedCost: number;
  byType: CostBreakdown[];
  byBot: BotCostBreakdown[];
  byModel: ModelCostBreakdown[];
  dailyCosts: DailyCost[];
  discountPercent?: number;
  discountedTotal?: number;
}

/**
 * Projected cost data from API
 */
interface ProjectedCost {
  projectedCost: number;
  averageDailyCost: number;
  sampledDays: number;
  confidence: 'low' | 'medium' | 'high';
}

/**
 * Date preset options
 */
type DatePreset = '7d' | '30d' | '90d' | 'custom';

// =============================================================================
// Constants
// =============================================================================

const USAGE_TYPE_LABELS: Record<UsageType, string> = {
  LLM_CHAT: 'LLM Chat',
  TRANSCRIPTION: 'Transcription',
  TTS: 'Text-to-Speech',
  IMAGE: 'Image Generation',
  EMBEDDING: 'Embeddings',
  SEARCH: 'Search',
};

const USAGE_TYPE_COLORS: Record<UsageType, { bg: string; bar: string }> = {
  LLM_CHAT: { bg: 'bg-blue-500/20', bar: 'bg-blue-500' },
  TRANSCRIPTION: { bg: 'bg-green-500/20', bar: 'bg-green-500' },
  TTS: { bg: 'bg-purple-500/20', bar: 'bg-purple-500' },
  IMAGE: { bg: 'bg-yellow-500/20', bar: 'bg-yellow-500' },
  EMBEDDING: { bg: 'bg-pink-500/20', bar: 'bg-pink-500' },
  SEARCH: { bg: 'bg-cyan-500/20', bar: 'bg-cyan-500' },
};

const CONFIDENCE_COLORS: Record<ProjectedCost['confidence'], { bg: string; text: string }> = {
  low: { bg: 'bg-red-900/20', text: 'text-red-400' },
  medium: { bg: 'bg-yellow-900/20', text: 'text-yellow-400' },
  high: { bg: 'bg-green-900/20', text: 'text-green-400' },
};

const DATE_PRESETS: { key: DatePreset; label: string; days: number }[] = [
  { key: '7d', label: '7 Days', days: 7 },
  { key: '30d', label: '30 Days', days: 30 },
  { key: '90d', label: '90 Days', days: 90 },
  { key: 'custom', label: 'Custom', days: 0 },
];

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount);
}

/**
 * Format large numbers with abbreviations
 */
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format short date for chart labels
 */
function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get date range based on preset
 */
function getDateRange(preset: DatePreset, customStart?: Date, customEnd?: Date): { start: Date; end: Date } {
  const end = new Date();
  end.setHours(23, 59, 59, 999);

  if (preset === 'custom' && customStart && customEnd) {
    return { start: customStart, end: customEnd };
  }

  const presetConfig = DATE_PRESETS.find((p) => p.key === preset);
  const days = presetConfig?.days || 30;

  const start = new Date();
  start.setDate(start.getDate() - days);
  start.setHours(0, 0, 0, 0);

  return { start, end };
}

/**
 * Calculate percentage of total
 */
function calculatePercentage(value: number, total: number): number {
  if (total === 0) return 0;
  return (value / total) * 100;
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Statistics card component
 */
interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color?: 'default' | 'green' | 'red' | 'yellow' | 'blue';
}

function StatCard({ title, value, subtitle, icon, color = 'default' }: StatCardProps) {
  const colorClasses = {
    default: 'text-white',
    green: 'text-green-400',
    red: 'text-red-400',
    yellow: 'text-yellow-400',
    blue: 'text-blue-400',
  };

  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-400">{title}</p>
          <p className={`mt-1 text-2xl font-bold ${colorClasses[color]}`}>
            {value}
          </p>
          {subtitle && (
            <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>
          )}
        </div>
        <div className="rounded-full bg-discord-light p-2.5 text-gray-400">
          {icon}
        </div>
      </div>
    </div>
  );
}

/**
 * Date preset selector component
 */
interface DatePresetSelectorProps {
  value: DatePreset;
  onChange: (preset: DatePreset) => void;
}

function DatePresetSelector({ value, onChange }: DatePresetSelectorProps) {
  return (
    <div className="flex items-center gap-2">
      {DATE_PRESETS.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === key
              ? 'bg-discord-blurple text-white'
              : 'bg-discord-light text-gray-300 hover:text-white'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Cost breakdown by type chart component
 */
interface TypeBreakdownChartProps {
  data: CostBreakdown[];
  totalCost: number;
}

function TypeBreakdownChart({ data, totalCost }: TypeBreakdownChartProps) {
  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No usage data available
      </div>
    );
  }

  // Sort by cost descending
  const sortedData = [...data].sort((a, b) => b.calculatedCost - a.calculatedCost);

  return (
    <div className="space-y-3">
      {sortedData.map((item) => {
        const percentage = calculatePercentage(item.calculatedCost, totalCost);
        const colors = USAGE_TYPE_COLORS[item.type] || { bg: 'bg-gray-500/20', bar: 'bg-gray-500' };

        return (
          <div key={item.type} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-300">{USAGE_TYPE_LABELS[item.type] || item.type}</span>
              <span className="text-white font-medium">
                {formatCurrency(item.calculatedCost)}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className={`flex-1 h-2 rounded-full ${colors.bg}`}>
                <div
                  className={`h-full rounded-full ${colors.bar} transition-all duration-500`}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 w-12 text-right">
                {percentage.toFixed(1)}%
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span>{formatNumber(item.count)} requests</span>
              {item.totalTokens > 0 && <span>{formatNumber(item.totalTokens)} tokens</span>}
              {item.totalDuration > 0 && <span>{item.totalDuration.toFixed(1)}s duration</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Bot cost breakdown table component
 */
interface BotBreakdownTableProps {
  data: BotCostBreakdown[];
  totalCost: number;
}

function BotBreakdownTable({ data, totalCost }: BotBreakdownTableProps) {
  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No bot usage data available
      </div>
    );
  }

  // Sort by cost descending
  const sortedData = [...data].sort((a, b) => b.cost - a.cost);

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-left text-sm text-gray-400 border-b border-discord-light">
            <th className="pb-2 font-medium">Bot</th>
            <th className="pb-2 font-medium text-right">Cost</th>
            <th className="pb-2 font-medium text-right">Tokens</th>
            <th className="pb-2 font-medium text-right">Requests</th>
            <th className="pb-2 font-medium text-right">Share</th>
          </tr>
        </thead>
        <tbody className="text-sm">
          {sortedData.map((item) => {
            const percentage = calculatePercentage(item.cost, totalCost);
            return (
              <tr key={item.botId} className="border-b border-discord-light/50">
                <td className="py-2 text-white font-medium">{item.botName}</td>
                <td className="py-2 text-right text-green-400">{formatCurrency(item.cost)}</td>
                <td className="py-2 text-right text-gray-300">{formatNumber(item.tokenCount)}</td>
                <td className="py-2 text-right text-gray-300">{formatNumber(item.requestCount)}</td>
                <td className="py-2 text-right text-gray-400">{percentage.toFixed(1)}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Model cost breakdown table component
 */
interface ModelBreakdownTableProps {
  data: ModelCostBreakdown[];
  totalCost: number;
}

function ModelBreakdownTable({ data, totalCost }: ModelBreakdownTableProps) {
  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No model usage data available
      </div>
    );
  }

  // Sort by cost descending
  const sortedData = [...data].sort((a, b) => b.cost - a.cost);

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-left text-sm text-gray-400 border-b border-discord-light">
            <th className="pb-2 font-medium">Model</th>
            <th className="pb-2 font-medium text-right">Cost</th>
            <th className="pb-2 font-medium text-right">Requests</th>
            <th className="pb-2 font-medium text-right">Share</th>
          </tr>
        </thead>
        <tbody className="text-sm">
          {sortedData.map((item) => {
            const percentage = calculatePercentage(item.cost, totalCost);
            return (
              <tr key={item.model} className="border-b border-discord-light/50">
                <td className="py-2 text-white font-mono text-xs">{item.model}</td>
                <td className="py-2 text-right text-green-400">{formatCurrency(item.cost)}</td>
                <td className="py-2 text-right text-gray-300">{formatNumber(item.count)}</td>
                <td className="py-2 text-right text-gray-400">{percentage.toFixed(1)}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Daily cost trend chart component (simple bar chart)
 */
interface DailyCostChartProps {
  data: DailyCost[];
}

function DailyCostChart({ data }: DailyCostChartProps) {
  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No daily cost data available
      </div>
    );
  }

  // Find max cost for scaling
  const maxCost = Math.max(...data.map((d) => d.cost), 0.01);

  // Show last 14 days if more data available
  const displayData = data.slice(-14);

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-1 h-32">
        {displayData.map((item) => {
          const height = (item.cost / maxCost) * 100;
          return (
            <div
              key={item.date}
              className="flex-1 flex flex-col items-center justify-end group"
            >
              <div
                className="w-full bg-discord-blurple rounded-t transition-all hover:bg-discord-blurple/80 cursor-pointer relative"
                style={{ height: `${Math.max(height, 2)}%` }}
              >
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-discord-darker border border-discord-light rounded px-2 py-1 text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  {formatCurrency(item.cost)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-1">
        {displayData.map((item, index) => (
          <div key={item.date} className="flex-1 text-center">
            {/* Show every other label to avoid crowding */}
            {index % 2 === 0 ? (
              <span className="text-[10px] text-gray-500">{formatShortDate(item.date)}</span>
            ) : (
              <span className="text-[10px] text-transparent">.</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Projected cost card component
 */
interface ProjectedCostCardProps {
  projectedCost: ProjectedCost | null;
  isLoading: boolean;
}

function ProjectedCostCard({ projectedCost, isLoading }: ProjectedCostCardProps) {
  if (isLoading) {
    return (
      <div className="rounded-lg bg-discord-darker border border-discord-light p-4 animate-pulse">
        <div className="h-4 w-32 bg-discord-light rounded mb-2" />
        <div className="h-8 w-24 bg-discord-light rounded mb-1" />
        <div className="h-3 w-40 bg-discord-light rounded" />
      </div>
    );
  }

  if (!projectedCost) {
    return (
      <div className="rounded-lg bg-discord-darker border border-discord-light p-4">
        <p className="text-sm text-gray-400">Unable to calculate projection</p>
      </div>
    );
  }

  const confidenceColors = CONFIDENCE_COLORS[projectedCost.confidence];

  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-gray-400">Projected Monthly Cost</p>
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium ${confidenceColors.bg} ${confidenceColors.text}`}
        >
          {projectedCost.confidence} confidence
        </span>
      </div>
      <p className="text-2xl font-bold text-white">
        {formatCurrency(projectedCost.projectedCost)}
      </p>
      <p className="text-xs text-gray-500 mt-1">
        Based on {projectedCost.sampledDays} days ({formatCurrency(projectedCost.averageDailyCost)}/day avg)
      </p>
    </div>
  );
}

/**
 * Loading skeleton component
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      {/* Stats skeleton */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-lg bg-discord-darker border border-discord-light p-4">
            <div className="h-4 w-24 bg-discord-light rounded mb-2" />
            <div className="h-8 w-16 bg-discord-light rounded" />
          </div>
        ))}
      </div>

      {/* Content skeleton */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
          <div className="h-6 w-32 bg-discord-light rounded mb-4" />
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i}>
                <div className="h-4 w-full bg-discord-light rounded mb-1" />
                <div className="h-2 w-full bg-discord-light rounded" />
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
          <div className="h-6 w-32 bg-discord-light rounded mb-4" />
          <div className="h-32 bg-discord-light rounded" />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

const CurrencyIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const TokenIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"
    />
  </svg>
);

const RequestIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const TrendIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
    />
  </svg>
);

const DownloadIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);

const RefreshIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

// =============================================================================
// Main Component
// =============================================================================

/**
 * Cost Reports page component
 *
 * Displays:
 * - Overview statistics (total cost, tokens, requests, average daily cost)
 * - Cost breakdown by service type with visual bar chart
 * - Cost breakdown by bot with table
 * - Cost breakdown by model with table
 * - Daily cost trend chart
 * - Projected monthly costs
 * - Export functionality
 */
export function CostReports() {
  // Auth context - user available for future permission checks
  useAuth();
  const { effectiveTenantId } = useTenant();

  // State
  const [report, setReport] = useState<CostReport | null>(null);
  const [projectedCost, setProjectedCost] = useState<ProjectedCost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProjectionLoading, setIsProjectionLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [datePreset, setDatePreset] = useState<DatePreset>('30d');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'type' | 'bot' | 'model'>('type');

  /**
   * Fetch cost report data
   */
  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const { start, end } = getDateRange(
        datePreset,
        customStartDate ? new Date(customStartDate) : undefined,
        customEndDate ? new Date(customEndDate) : undefined
      );

      // Build query params
      const params = new URLSearchParams();
      params.set('startDate', start.toISOString());
      params.set('endDate', end.toISOString());

      if (effectiveTenantId) {
        params.set('tenantId', effectiveTenantId);
      }

      const result = await apiClient.get<CostReport>(`/reports/costs?${params.toString()}`);

      if (isSuccess(result)) {
        setReport(result.data);
      } else {
        setError(result.error.message || 'Failed to fetch cost report');
      }
    } catch (err) {
      setError('Failed to load cost report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [datePreset, customStartDate, customEndDate, effectiveTenantId]);

  /**
   * Fetch projected cost data
   */
  const fetchProjectedCost = useCallback(async () => {
    setIsProjectionLoading(true);

    const apiClient = getApiClient();

    try {
      const params = new URLSearchParams();
      params.set('daysToSample', '7');

      if (effectiveTenantId) {
        params.set('tenantId', effectiveTenantId);
      }

      const result = await apiClient.get<ProjectedCost>(`/reports/projected-costs?${params.toString()}`);

      if (isSuccess(result)) {
        setProjectedCost(result.data);
      } else {
        setProjectedCost(null);
      }
    } catch (err) {
      setProjectedCost(null);
    } finally {
      setIsProjectionLoading(false);
    }
  }, [effectiveTenantId]);

  /**
   * Initial data fetch
   */
  useEffect(() => {
    void fetchReport();
    void fetchProjectedCost();
  }, [fetchReport, fetchProjectedCost]);

  /**
   * Handle date preset change
   */
  const handleDatePresetChange = useCallback((preset: DatePreset) => {
    setDatePreset(preset);
  }, []);

  /**
   * Calculate summary statistics
   */
  const summaryStats = useMemo(() => {
    if (!report) {
      return {
        totalCost: 0,
        totalTokens: 0,
        totalRequests: 0,
        averageDailyCost: 0,
      };
    }

    const totalTokens = report.byType.reduce((sum, item) => sum + item.totalTokens, 0);
    const totalRequests = report.byType.reduce((sum, item) => sum + item.count, 0);
    const daysInPeriod = report.dailyCosts.length || 1;
    const averageDailyCost = report.totalCost / daysInPeriod;

    return {
      totalCost: report.totalCost,
      totalTokens,
      totalRequests,
      averageDailyCost,
    };
  }, [report]);

  /**
   * Export report as CSV
   */
  const handleExport = useCallback(() => {
    if (!report) return;

    // Build CSV content
    const rows: string[][] = [];

    // Header
    rows.push(['Cost Report']);
    rows.push(['Period', `${formatDate(report.periodStart)} - ${formatDate(report.periodEnd)}`]);
    rows.push(['Total Cost', formatCurrency(report.totalCost)]);
    rows.push([]);

    // By Type
    rows.push(['Cost by Service Type']);
    rows.push(['Type', 'Cost', 'Requests', 'Tokens']);
    report.byType.forEach((item) => {
      rows.push([
        USAGE_TYPE_LABELS[item.type] || item.type,
        item.calculatedCost.toString(),
        item.count.toString(),
        item.totalTokens.toString(),
      ]);
    });
    rows.push([]);

    // By Bot
    rows.push(['Cost by Bot']);
    rows.push(['Bot', 'Cost', 'Requests', 'Tokens']);
    report.byBot.forEach((item) => {
      rows.push([
        item.botName,
        item.cost.toString(),
        item.requestCount.toString(),
        item.tokenCount.toString(),
      ]);
    });
    rows.push([]);

    // Daily costs
    rows.push(['Daily Costs']);
    rows.push(['Date', 'Cost', 'Requests']);
    report.dailyCosts.forEach((item) => {
      rows.push([item.date, item.cost.toString(), item.count.toString()]);
    });

    // Convert to CSV string
    const csvContent = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cost-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [report]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Cost Reports</h1>
            <p className="mt-1 text-gray-400">
              Track and analyze your AI service usage costs
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={!report || isLoading}
              className="inline-flex items-center gap-2 rounded-md bg-discord-light px-3 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DownloadIcon />
              Export
            </button>
            <button
              onClick={() => {
                void fetchReport();
                void fetchProjectedCost();
              }}
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-discord-blurple transition-colors disabled:opacity-50"
            >
              <RefreshIcon />
              Refresh
            </button>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <DatePresetSelector value={datePreset} onChange={handleDatePresetChange} />

          {datePreset === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="rounded-md bg-discord-light border border-discord-light px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-discord-blurple"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="rounded-md bg-discord-light border border-discord-light px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-discord-blurple"
              />
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-200">{error}</p>
              </div>
              <button
                onClick={() => void fetchReport()}
                className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="Total Cost"
                value={formatCurrency(summaryStats.totalCost)}
                subtitle={report?.discountedTotal !== undefined
                  ? `After ${report.discountPercent}% discount: ${formatCurrency(report.discountedTotal)}`
                  : undefined
                }
                icon={<CurrencyIcon />}
                color="green"
              />
              <StatCard
                title="Tokens Used"
                value={formatNumber(summaryStats.totalTokens)}
                icon={<TokenIcon />}
                color="blue"
              />
              <StatCard
                title="Total Requests"
                value={formatNumber(summaryStats.totalRequests)}
                icon={<RequestIcon />}
              />
              <StatCard
                title="Avg Daily Cost"
                value={formatCurrency(summaryStats.averageDailyCost)}
                icon={<TrendIcon />}
                color="yellow"
              />
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Cost Breakdown Section */}
              <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white">Cost Breakdown</h2>
                  <div className="flex items-center gap-1">
                    {(['type', 'bot', 'model'] as const).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                          activeTab === tab
                            ? 'bg-discord-blurple text-white'
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        {tab === 'type' ? 'By Type' : tab === 'bot' ? 'By Bot' : 'By Model'}
                      </button>
                    ))}
                  </div>
                </div>

                {activeTab === 'type' && report && (
                  <TypeBreakdownChart data={report.byType} totalCost={report.totalCost} />
                )}
                {activeTab === 'bot' && report && (
                  <BotBreakdownTable data={report.byBot} totalCost={report.totalCost} />
                )}
                {activeTab === 'model' && report && (
                  <ModelBreakdownTable data={report.byModel} totalCost={report.totalCost} />
                )}
              </div>

              {/* Daily Trend & Projection */}
              <div className="space-y-6">
                {/* Daily Cost Trend */}
                <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
                  <h2 className="text-lg font-semibold text-white mb-4">Daily Cost Trend</h2>
                  {report && <DailyCostChart data={report.dailyCosts} />}
                </div>

                {/* Projected Cost */}
                <ProjectedCostCard projectedCost={projectedCost} isLoading={isProjectionLoading} />
              </div>
            </div>

            {/* Period Summary */}
            {report && (
              <div className="text-sm text-gray-400">
                Report period: {formatDate(report.periodStart)} - {formatDate(report.periodEnd)} ·{' '}
                {report.dailyCosts.length} days of data
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default CostReports;
